import React, { useState, useEffect, useRef } from 'react';
import { useParticipants, useLocalParticipant, useRemoteParticipants } from '@livekit/components-react';
import { Track, DataPacket_Kind } from 'livekit-client';
import './PhoneCall.css';

const PhoneCall = ({ isHost, roomInfo, roomAlias, meetingCode, initialAudio, initialVideo, onLeave, hideChat }) => {
  const [linkCopied, setLinkCopied] = useState(false);
  const participants = useParticipants();
  const remoteParticipants = useRemoteParticipants();
  const { localParticipant } = useLocalParticipant();
  
  const [muted, setMuted] = useState(false);
  const [speakerOn, setSpeakerOn] = useState(false);
  const [videoOn, setVideoOn] = useState(false);
  const [duration, setDuration] = useState(0);
  const [connected, setConnected] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  
  const videoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const startRef = useRef(null);

  // Sync mic/video state with LiveKit on mount
  useEffect(function() {
    if (!localParticipant) return;
    var micEnabled = localParticipant.isMicrophoneEnabled;
    setMuted(!micEnabled);
    if (!micEnabled && initialAudio) {
      localParticipant.setMicrophoneEnabled(true).catch(function(){});
      setMuted(false);
    }
    var camEnabled = localParticipant.isCameraEnabled;
    setVideoOn(camEnabled);
  }, []);

  var localIdentity = localParticipant ? localParticipant.identity : '';

  // Detect other host
  const isOtherHostPresent = remoteParticipants.some(function(p) {
    try {
      var meta = typeof p.metadata === 'string' ? JSON.parse(p.metadata) : p.metadata;
      if (meta && meta.isHost === true) return true;
    } catch (e) {}
    if (p.identity && p.identity.startsWith('host-')) return true;
    if (p.name && p.name.includes('(Host)')) return true;
    return false;
  });

  const hostPresent = isHost || isOtherHostPresent;

  // Auto-publish when host arrives
  useEffect(function() {
    if (hostPresent && !isHost && localParticipant) {
      if (initialAudio && !localParticipant.isMicrophoneEnabled) {
        localParticipant.setMicrophoneEnabled(true).catch(function(){});
      }
    }
  }, [hostPresent, isHost, initialAudio, localParticipant]);

  // Mark connected when host is present or remote participant count > 0
  useEffect(function() {
    if (hostPresent || (remoteParticipants.length > 0 && !isHost)) {
      if (!connected) {
        setConnected(true);
        startRef.current = Date.now();
      }
    }
  }, [hostPresent, remoteParticipants.length, connected, isHost]);

  // Duration timer
  useEffect(function() {
    if (!connected) return;
    var timer = setInterval(function() {
      setDuration(Math.floor((Date.now() - startRef.current) / 1000));
    }, 1000);
    return function() { clearInterval(timer); };
  }, [connected]);

  const formatDuration = function(s) {
    var m = Math.floor(s / 60);
    var sec = s % 60;
    return m + ':' + String(sec).padStart(2, '0');
  };

  const toggleMute = function() {
    if (localParticipant) {
      var newVal = !muted;
      localParticipant.setMicrophoneEnabled(!newVal).catch(function(){});
      setMuted(newVal);
    }
  };

  const toggleSpeaker = function() {
    var newVal = !speakerOn;
    setSpeakerOn(newVal);
    var sink = newVal ? 'speaker' : 'default';
    document.querySelectorAll('audio').forEach(function(audio) {
      if (audio.setSinkId) {
        audio.setSinkId(sink).catch(function(){});
      }
    });
  };

  const toggleVideo = function() {
    if (!localParticipant) return;
    var newVal = !videoOn;
    if (!newVal) {
      // Turn OFF: unpublish camera track and stop all local video tracks
      var pub = localParticipant.getTrackPublication(Track.Source.Camera);
      if (pub && pub.track) {
        localParticipant.unpublishTrack(pub.track).catch(function(){});
        pub.track.stop();
      }
    }
    localParticipant.setCameraEnabled(newVal).catch(function(){});
    setVideoOn(newVal);
  };

  const flipCamera = function() {
    if (!localParticipant) return;
    var camPub = localParticipant.getTrackPublication(Track.Source.Camera);
    if (!camPub || !camPub.track) return;
    var track = camPub.track;
    var settings = track.getSettings && track.getSettings();
    var currentFacing = (settings && settings.facingMode) || 'user';
    var newFacing = currentFacing === 'user' ? 'environment' : 'user';
    navigator.mediaDevices.getUserMedia({ video: { facingMode: newFacing === 'environment' ? { exact: 'environment' } : 'user' } })
      .then(function(stream) {
        track.stop();
        var newTrack = stream.getVideoTracks()[0];
        if (newTrack) localParticipant.publishTrack(newTrack);
        setVideoOn(true);
      }).catch(function() {
        // No back camera — keep current track, reset to user-facing
        if (newFacing === 'environment') {
          navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } }).then(function(stream) {
            if (!stream.getVideoTracks()[0]) return;
            localParticipant.publishTrack(stream.getVideoTracks()[0]);
            setVideoOn(true);
          }).catch(function(){});
        }
      });
  };

  // Attach local video track to small self-view
  useEffect(function() {
    if (!localParticipant || !videoRef.current) return;
    var el = videoRef.current;
    var trackPub = localParticipant.getTrackPublication(Track.Source.Camera);
    if (!trackPub) return;

    var onAttachLocal = function() {
      if (trackPub.track && videoOn && el) {
        trackPub.track.attach(el);
        if (el.paused) el.play().catch(function(){});
      }
    };

    try { trackPub.on('subscribed', onAttachLocal); } catch(e) {}
    try { trackPub.on('trackPublished', onAttachLocal); } catch(e) {}
    onAttachLocal();

    return function() {
      try { trackPub.off('subscribed', onAttachLocal); } catch(e) {}
      try { trackPub.off('trackPublished', onAttachLocal); } catch(e) {}
      if (el) el.srcObject = null;
    };
  }, [localParticipant, videoOn]);

  // Subscribe and attach remote camera tracks (ONLY from remote participants)
  useEffect(function() {
    var videoEl = remoteVideoRef.current;
    if (!videoEl) return;

    function attachTrack(track) {
      if (track && !track.isMuted && videoEl) {
        track.attach(videoEl);
        if (videoEl.paused) videoEl.play().catch(function(){});
      }
    }

    var cleanups = [];
    remoteParticipants.forEach(function(p) {
      var onTrackSubscribed = function(track) {
        if (track.kind === 'video') attachTrack(track);
      };
      var onTrackMuted = function() { if (videoEl) videoEl.srcObject = null; };
      var onTrackUnmuted = function(pub) {
        if (pub && pub.track) attachTrack(pub.track);
      };

      try { p.on('trackSubscribed', onTrackSubscribed); } catch(e) {}
      try { p.on('trackMuted', onTrackMuted); } catch(e) {}
      try { p.on('trackUnmuted', onTrackUnmuted); } catch(e) {}

      // Attach already-subscribed tracks
      p.getTrackPublications().forEach(function(pub) {
        if (pub.kind === 'video' && pub.track && !pub.track.isMuted) {
          attachTrack(pub.track);
        }
      });

      cleanups.push(function() {
        try { p.off('trackSubscribed', onTrackSubscribed); } catch(e) {}
        try { p.off('trackMuted', onTrackMuted); } catch(e) {}
        try { p.off('trackUnmuted', onTrackUnmuted); } catch(e) {}
      });
    });

    return function() {
      cleanups.forEach(function(fn) { fn(); });
      if (videoEl) videoEl.srcObject = null;
    };
  }, [remoteParticipants]);

  var hasRemoteVideo = remoteParticipants.some(function(p) {
    return p.getTrackPublications().some(function(pub) { return pub.kind === 'video' && pub.track && !pub.track.isMuted; });
  });

  // Chat: receive messages via LiveKit data channel
  useEffect(function() {
    var lroom = localParticipant && localParticipant.room;
    if (!lroom) return;
    var handler = function(payload, participant, kind) {
      try {
        var data = JSON.parse(new TextDecoder().decode(payload));
        if (data.type === 'chat') {
          setChatMessages(function(prev) {
            return prev.concat([{ from: participant.identity, text: data.text, time: Date.now() }]);
          });
        }
      } catch(e) {}
    };
    try { lroom.on('dataReceived', handler); } catch(e) {}
    return function() { try { lroom.off('dataReceived', handler); } catch(e) {} };
  }, [localParticipant]);

  var sendChat = function() {
    var text = chatInput.trim();
    if (!text || !localParticipant || !localParticipant.room) return;
    var encoded = new TextEncoder().encode(JSON.stringify({ type: 'chat', text: text }));
    localParticipant.room.localParticipant.publishData(encoded, DataPacket_Kind.RELIABLE);
    setChatMessages(function(prev) { return prev.concat([{ from: 'Me', text: text, time: Date.now() }]); });
    setChatInput('');
  };

  var callerName = isHost ? (roomInfo?.room_name || 'Your Room') : (roomInfo?.meeting_name || roomInfo?.room_name || 'Meeting');
  var initials = callerName.replace(/[^A-Za-z0-9]/g, '').slice(0, 2).toUpperCase() || '?';

  var showVideo = videoOn || hasRemoteVideo;

  return (
    <div className="phone-call phone-call--call">
      {/* Chat button — hidden when external chat (LinkTimeChat) is present */}
      {!hideChat && (
      <button className="phone-call-chat-btn" title="Chat" onClick={() => setChatOpen(!chatOpen)}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
        </svg>
      </button>
      )}

      {/* Top info */}
      <div className="phone-call-top">
        <h1 className="phone-call-name">
          {callerName}
          {isHost && <span className="phone-call-host-badge">HOST</span>}
        </h1>
        <p className="phone-call-status">
          {connected ? formatDuration(duration) : 'Connecting...'}
        </p>
        {isHost && (
          <div style={{display:'flex',gap:8,justifyContent:'center',marginTop:6}}>
            <a href="/linktime" className="phone-call-back-rooms">← Back to rooms</a>
            <button className="phone-call-back-rooms" onClick={() => {
              var url = window.location.origin + '/link/' + roomAlias + '/' + meetingCode + '?force_guest=1';
              navigator.clipboard.writeText(url);
              setLinkCopied(true);
              setTimeout(function() { setLinkCopied(false); }, 2000);
            }} style={{border:'none',cursor:'pointer',background:'none',fontSize:12,color:'#4498A2'}}>{linkCopied ? '✓ Copied!' : '📋 Copy invite link'}</button>
          </div>
        )}
      </div>

      {/* Middle: video, avatar, or waiting */}
      <div className="phone-call-spacer">
        {showVideo ? (
          <div className="phone-call-video-area">
            <video ref={remoteVideoRef} autoPlay playsInline className="phone-call-video-remote" />
            {videoOn && <video ref={videoRef} autoPlay playsInline muted className="phone-call-video-self" />}
            {videoOn && (
              <button className="phone-call-flip-btn" onClick={flipCamera}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 014-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 01-4 4H3"/></svg>
              </button>
            )}
          </div>
        ) : (
          <div className="phone-call-avatar">{initials}</div>
        )}

        {!hostPresent && !connected && (
          <div className="phone-call-waiting">
            Waiting for host
            <span className="phone-call-waiting-dots">
              <span /><span /><span />
            </span>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="phone-call-controls">
        <button className={`phone-call-ctrl mute ${muted ? 'on' : ''}`} onClick={toggleMute}>
          {muted ? (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="1" y1="1" x2="23" y2="23"/><path d="M9 9v3a3 3 0 005.12 2.12M15 9.34V4a3 3 0 00-5.94-.6"/><path d="M17 16.95A7 7 0 015 12v-2m14 0v2a7 7 0 01-.11 1.23"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
          ) : (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/><path d="M19 10v2a7 7 0 01-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>
          )}
        </button>

        <button className="phone-call-ctrl end" onClick={onLeave}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 9c-1.6 0-3.15.25-4.6.72v2.54c0 .24-.14.46-.37.54l-2.9 1.17a1.5 1.5 0 00-.93 1.47v1.9c0 .46.39.83.85.75 2.8-.5 5.6-.5 8.4 0 .46.08.85-.29.85-.75v-1.9c0-.64-.41-1.21-1.02-1.42l-2.59-1.04a.58.58 0 01-.37-.54v-2.63C10.6 9.19 10.34 9 10 9h4z"/>
          </svg>
        </button>

        <button className={`phone-call-ctrl camera ${videoOn ? 'on' : ''}`} onClick={toggleVideo}>
          {videoOn ? (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M15 10l4.55-3.4A1 1 0 0121 7.4v9.2a1 1 0 01-1.45.9L15 14v-4z"/><rect x="3" y="6" width="12" height="12" rx="2"/></svg>
          ) : (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="1" y1="1" x2="23" y2="23"/><path d="M15 10l4.55-3.4A1 1 0 0121 7.4v9.2a1 1 0 01-1.45.9L15 14v-4z"/><rect x="3" y="6" width="12" height="12" rx="2"/></svg>
          )}
        </button>

        <button className={`phone-call-ctrl speaker ${speakerOn ? 'on' : ''}`} onClick={toggleSpeaker}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07"/></svg>
        </button>
      </div>

      {/* Chat overlay */}
      {chatOpen && !hideChat && (
        <div className="phone-call-chat">
          <div className="phone-call-chat-header">
            <span>Chat</span>
            <button className="phone-call-chat-close" onClick={() => setChatOpen(false)}>&times;</button>
          </div>
          <div className="phone-call-chat-messages">
            {chatMessages.length === 0 && <p className="phone-call-chat-empty">No messages yet</p>}
            {chatMessages.map(function(m, i) {
              return (
                <div key={i} className={'phone-call-chat-msg ' + (m.from === 'Me' ? 'me' : '')}>
                  <span className="phone-call-chat-from">{m.from}</span>
                  <span className="phone-call-chat-text">{m.text}</span>
                </div>
              );
            })}
          </div>
          <form className="phone-call-chat-input" onSubmit={function(e){ e.preventDefault(); sendChat(); }}>
            <input value={chatInput} onChange={function(e){ setChatInput(e.target.value); }} placeholder="Type a message..." />
            <button type="submit">Send</button>
          </form>
        </div>
      )}
    </div>
  );
};

export default PhoneCall;
